import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import analyticsService from '../../api/services/analyticsService';
import hostService from '../../api/services/hostService';
import { formatCurrency, handleApiError } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import contestService from '../../api/services/contestService';
import axiosInstance from '../../api/axios';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = { ACTIVE: 'bg-green-100 text-green-800', PAUSED: 'bg-yellow-100 text-yellow-800', DRAFT: 'bg-gray-100 text-gray-700', ENDED: 'bg-red-100 text-red-800' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

const BreakdownRow = ({ label, value, color, dotColor }) => (
  <div className="flex items-center justify-between text-xs mt-1">
    <span className="flex items-center gap-1.5 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dotColor }} />{label}
    </span>
    <span className={`font-semibold ${color}`}>{value}</span>
  </div>
);

const ClickableCard = ({ children, onClick, colorClass }) => (
  <div onClick={onClick} className={`cursor-pointer rounded-lg border bg-white shadow-sm transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] border-l-4 ${colorClass} p-4`}>
    {children}
    <div className="mt-3 flex items-center gap-1 text-xs font-medium text-gray-400">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
      Tap to view details
    </div>
  </div>
);

const DetailModal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5">{children}</div>
      </div>
    </div>
  );
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <span className="text-xl">🥇</span>;
  if (rank === 2) return <span className="text-xl">🥈</span>;
  if (rank === 3) return <span className="text-xl">🥉</span>;
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">{rank}</span>;
};

// ─── Live Results Modal ───────────────────────────────────────────────────────
const LiveResultsModal = ({ open, onClose, contest }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'excel' | null

  useEffect(() => {
    if (open && contest) { setResults(null); fetchResults(); }
  }, [open, contest]);

  // const fetchResults = async () => {
  //   try {
  //     setLoading(true);
  //     // try public endpoint first (has voteCount on contestants)
  //     const res = await axiosInstance.get(`/contests/${contest.id}/public`);
  //     if (res.data?.success) { setResults(res.data.data); return; }
  //   } catch {}
  //   try {
  //     const res = await axiosInstance.get(`/contests/${contest.id}`);
  //     if (res.data?.success) setResults(res.data.data);
  //   } catch { setResults(null); }
  //   finally { setLoading(false); }
  // };

  const fetchResults = async () => {
    try {
      setLoading(true);
      setResults(null);
      // Use the new /results endpoint — bypasses showResults + endDate + status
      const res = await axiosInstance.get(`/contests/${contest.id}/results`);
      if (res.data?.success) { setResults(res.data.data); return; }
    } catch {
      // Fallback to admin detail (no vote counts but at least shows the contest)
      try {
        const res = await axiosInstance.get(`/contests/${contest.id}`);
        if (res.data?.success) setResults(res.data.data);
      } catch { setResults(null); }
    } finally { setLoading(false); }
  };

  const votePrice = results?.votePrice || contest?.votePrice || 1;

  const rankedCategories = (results?.categories || []).map(cat => ({
    ...cat,
    contestants: [...(cat.contestants || [])]
      .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
      .map((c, idx) => ({ ...c, rank: idx + 1 })),
  }));

  const totalVotes = rankedCategories.reduce(
    (s, cat) => s + cat.contestants.reduce((cs, c) => cs + (c.voteCount || 0), 0), 0
  );

  // ── PDF export ──────────────────────────────────────────────────────────────
  const exportPDF = () => {
    setExporting('pdf');
    const title = results?.title || contest?.title || 'Contest';
    let html = `<!DOCTYPE html><html><head><title>Vote Summary - ${title}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:24px;color:#1f2937}
      h1{color:#dc2626;font-size:22px;margin-bottom:4px}
      .sub{color:#6b7280;font-size:12px;margin-bottom:16px}
      .summary{display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap}
      .sbox{background:#f3f4f6;border-radius:8px;padding:10px 14px;min-width:100px}
      .sbox label{font-size:10px;color:#9ca3af;display:block}
      .sbox span{font-size:16px;font-weight:bold;color:#1f2937}
      .cat{margin-bottom:20px}
      .cat-hd{background:#1d4ed8;color:white;padding:7px 10px;border-radius:6px 6px 0 0;font-size:12px;font-weight:bold;display:flex;justify-content:space-between}
      table{width:100%;border-collapse:collapse}
      th{background:#eff6ff;padding:6px 10px;text-align:left;font-size:10px;color:#1d4ed8;font-weight:bold}
      td{padding:6px 10px;font-size:11px;border-bottom:1px solid #f3f4f6}
      .r1 td{background:#fef9c3;font-weight:bold}
      .r2 td{background:#f9fafb}
      .r3 td{background:#fff7ed}
      .footer{margin-top:16px;font-size:10px;color:#9ca3af;text-align:center}
      .printbtn{margin-bottom:12px;padding:7px 14px;background:#dc2626;color:white;border:none;border-radius:6px;cursor:pointer;font-size:12px}
      @media print{.printbtn{display:none}}
    </style></head><body>
    <button class="printbtn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <h1>Vote Summary</h1>
    <div class="sub">${title} &nbsp;·&nbsp; Generated: ${new Date().toLocaleString()}</div>
    <div class="summary">
      <div class="sbox"><label>Total Votes</label><span>${totalVotes.toLocaleString()}</span></div>
      <div class="sbox"><label>Total Revenue</label><span>GHS ${(totalVotes * votePrice).toFixed(2)}</span></div>
      <div class="sbox"><label>Vote Price</label><span>GHS ${votePrice}</span></div>
      <div class="sbox"><label>Categories</label><span>${rankedCategories.length}</span></div>
    </div>`;

    rankedCategories.forEach(cat => {
      const catTotal = cat.contestants.reduce((s, c) => s + (c.voteCount || 0), 0);
      html += `<div class="cat"><div class="cat-hd"><span>${cat.name}</span><span>${catTotal.toLocaleString()} votes</span></div>
      <table><tr><th>Rank</th><th>Contestant</th><th>Votes</th><th>%</th><th>Revenue (GHS)</th></tr>`;
      cat.contestants.forEach(c => {
        const pct = catTotal > 0 ? ((c.voteCount || 0) / catTotal * 100).toFixed(1) : '0.0';
        const rev = ((c.voteCount || 0) * votePrice).toFixed(2);
        const rl = c.rank === 1 ? '🥇 1st' : c.rank === 2 ? '🥈 2nd' : c.rank === 3 ? '🥉 3rd' : `#${c.rank}`;
        const cls = c.rank <= 3 ? `r${c.rank}` : '';
        html += `<tr class="${cls}"><td>${rl}</td><td>${c.name}</td><td>${(c.voteCount || 0).toLocaleString()}</td><td>${pct}%</td><td>${rev}</td></tr>`;
      });
      html += `</table></div>`;
    });

    html += `<div class="footer">Generated by eBallot Box</div></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setExporting(null);
  };

  // ── Excel export ────────────────────────────────────────────────────────────
  const exportExcel = async () => {
    try {
      setExporting('excel');
      // SheetJS via CDN
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      document.head.appendChild(script);
      await new Promise((res, rej) => { script.onload = res; script.onerror = rej; });

      const XLSX = window.XLSX;
      const wb = XLSX.utils.book_new();
      const title = results?.title || contest?.title || 'Contest';

      // Summary sheet
      const sumData = [
        ['Vote Summary Report'],
        [`Contest: ${title}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
        ['Total Votes', totalVotes],
        ['Total Revenue (GHS)', (totalVotes * votePrice).toFixed(2)],
        ['Vote Price (GHS)', votePrice],
        ['Total Categories', rankedCategories.length],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sumData), 'Summary');

      // Results sheet
      const rows = [['Rank', 'Category', 'Contestant', 'Votes', 'Vote %', 'Revenue (GHS)']];
      rankedCategories.forEach(cat => {
        const catTotal = cat.contestants.reduce((s, c) => s + (c.voteCount || 0), 0);
        cat.contestants.forEach(c => {
          const pct = catTotal > 0 ? ((c.voteCount || 0) / catTotal * 100).toFixed(1) : '0.0';
          rows.push([c.rank, cat.name, c.name, c.voteCount || 0, `${pct}%`, ((c.voteCount || 0) * votePrice).toFixed(2)]);
        });
        rows.push([]);
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Results');
      XLSX.writeFile(wb, `Vote Summary - ${title}.xlsx`);
    } catch (e) {
      console.error('Excel export failed:', e);
      alert('Excel export failed. Try the PDF option instead.');
    } finally {
      setExporting(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">🏆 Live Results</h2>
            <p className="text-xs text-gray-500 truncate mt-0.5">{results?.title || contest?.title}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            <button onClick={exportExcel} disabled={!!exporting || loading || !results}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40">
              {exporting === 'excel'
                ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : '📊'} Excel
            </button>
            <button onClick={exportPDF} disabled={!!exporting || loading || !results}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40">
              {exporting === 'pdf'
                ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : '📄'} PDF
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-500">Loading live results...</p>
            </div>
          ) : !results ? (
            <p className="text-center text-gray-500 py-16">Could not load results for this contest.</p>
          ) : (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { label: 'Total Votes', value: totalVotes.toLocaleString(), color: 'text-blue-600' },
                  { label: 'Total Revenue', value: `GHS ${(totalVotes * votePrice).toFixed(2)}`, color: 'text-green-600' },
                  { label: 'Categories', value: rankedCategories.length, color: 'text-purple-600' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                    <p className="text-xs text-gray-400 mb-1">{label}</p>
                    <p className={`text-lg font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Categories */}
              {rankedCategories.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No categories found.</p>
              ) : (
                <div className="space-y-5">
                  {rankedCategories.map(cat => {
                    const catTotal = cat.contestants.reduce((s, c) => s + (c.voteCount || 0), 0);
                    const maxV = cat.contestants[0]?.voteCount || 1;
                    return (
                      <div key={cat.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Category header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between">
                          <h3 className="text-sm font-bold text-white truncate">{cat.name}</h3>
                          <span className="text-xs text-blue-100 flex-shrink-0 ml-2">
                            {catTotal.toLocaleString()} votes · {cat.contestants.length} contestants
                          </span>
                        </div>

                        {/* Contestants */}
                        <div className="divide-y divide-gray-50">
                          {cat.contestants.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">No contestants</p>
                          ) : cat.contestants.map(c => {
                            const pct = catTotal > 0 ? ((c.voteCount || 0) / catTotal * 100).toFixed(1) : '0.0';
                            const rev = (c.voteCount || 0) * votePrice;
                            const barPct = maxV > 0 ? ((c.voteCount || 0) / maxV * 100) : 0;
                            const rowBg = c.rank === 1 ? 'bg-yellow-50' : c.rank === 2 ? 'bg-gray-50' : c.rank === 3 ? 'bg-orange-50' : 'bg-white';
                            const barCol = c.rank === 1 ? 'from-yellow-400 to-yellow-500' : c.rank === 2 ? 'from-gray-300 to-gray-400' : c.rank === 3 ? 'from-orange-400 to-orange-500' : 'from-blue-400 to-blue-500';
                            return (
                              <div key={c.id} className={`${rowBg} px-4 py-3`}>
                                <div className="flex items-center gap-3">
                                  {/* Rank */}
                                  <div className="flex-shrink-0 w-7 text-center"><RankBadge rank={c.rank} /></div>

                                  {/* Photo */}
                                  {c.photo ? (
                                    <img
                                      src={c.photo.startsWith('http') ? c.photo : `${process.env.REACT_APP_BASE_URL || ''}/${c.photo}`}
                                      alt={c.name}
                                      className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm"
                                      onError={e => { e.target.style.display = 'none'; }}
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded-full flex-shrink-0 bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-sm">
                                      {c.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                  )}

                                  {/* Name + bar + stats */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className={`text-sm truncate pr-2 ${c.rank <= 3 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                                        {c.name}
                                      </span>
                                      <div className="flex-shrink-0 text-right">
                                        <span className="text-sm font-bold text-gray-900">{(c.voteCount || 0).toLocaleString()}</span>
                                        <span className="text-xs text-gray-400 ml-1">({pct}%)</span>
                                      </div>
                                    </div>
                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                      <div className={`h-full rounded-full bg-gradient-to-r ${barCol} transition-all duration-700`} style={{ width: `${Math.max(barPct, 2)}%` }} />
                                    </div>
                                    <p className="text-xs text-green-600 font-medium mt-0.5">
                                      {formatCurrency(rev)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const StatisticsPage = () => {
  const { user } = useAuth();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  // const [stats, setStats] = useState({
  //   totalHosts: 0, totalContests: 0, activeContests: 0,
  //   totalCategories: 0, totalContestants: 0,
  //   totalVotes: 0, paidVotes: 0, manualVotes: 0,
  //   totalRevenue: 0, totalPayments: 0,
  //   pausedContests: 0, draftContests: 0, endedContests: 0,
  // });
  const [stats, setStats] = useState({
    totalHosts: 0, totalContests: 0, activeContests: 0,
    totalCategories: 0, totalContestants: 0,
    totalVotes: 0, paidVotes: 0, manualVotes: 0,
    totalRevenue: 0, totalPayments: 0,
    failedPayments: 0, pendingPayments: 0,
    pausedContests: 0, draftContests: 0, endedContests: 0,
    ussdCount: 0, ussdAmount: 0,
    paystackCount: 0, paystackAmount: 0,
  });
  const [topContests, setTopContests] = useState([]);
  const [recentActivity, setRecentActivity] = useState({ payments: [], votes: [] });
  const [modal, setModal] = useState(null); // 'contests' | 'votes' | 'revenue'
  const [selectedContest, setSelectedContest] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
  const isCompanyAdmin = user?.role === USER_ROLES.COMPANY_ADMIN;
  const isSubAdmin = user?.role === USER_ROLES.SUB_ADMIN;

  useEffect(() => { fetchStatistics(); }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      if (isSuperAdmin) await fetchGlobalAnalytics();
      else await fetchUserAnalytics();
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Super Admin ─────────────────────────────────────────────────────────────
  const fetchGlobalAnalytics = async () => {
    const response = await analyticsService.getGlobalAnalytics();
    const data = response.data;

    console.log('First contest analytics:', JSON.stringify(data.contestAnalytics?.[0], null, 2));

    let hosts = [];
    try { const r = await hostService.getAllHosts({ page: 1, pageSize: 1000 }); hosts = r.data || r.hosts || []; }
    catch { try { const r = await hostService.getMyHosts(); hosts = r.data || r.hosts || []; } catch { } }

    const uniqueHosts = new Set();
    if (hosts.length === 0 && data.contestAnalytics)
      data.contestAnalytics.forEach(c => { if (c.contest?.host?.id) uniqueHosts.add(c.contest.host.id); });

    const totalVotes = data.summary.totalVotes || 0;
    const totalRevenue = data.summary.totalRevenue || 0;
    const paidVotes = (data.contestAnalytics || []).reduce((sum, c) =>
      sum + Math.round((c.revenue || 0) / (c.contest?.votePrice || 1)), 0);
    const manualVotes = Math.max(0, totalVotes - paidVotes);

    setStats({
      totalHosts: hosts.length > 0 ? hosts.length : uniqueHosts.size,
      totalContests: data.summary.totalContests || 0,
      activeContests: data.summary.activeContests || 0,
      totalVotes, paidVotes, manualVotes, totalRevenue,
      // totalPayments:    data.summary.totalPayments  || 0,
      totalPayments: data.summary.totalPayments || 0,
      failedPayments: data.summary.failedPayments || 0,
      failedAmount: data.summary.failedAmount || 0,
      pendingPayments: data.summary.pendingPayments || 0,
      pendingAmount: data.summary.pendingAmount || 0,
      completedAmount: data.summary.completedAmount || 0,
      ussdCount: data.summary.ussdCount || 0,
      ussdAmount: data.summary.ussdAmount || 0,
      paystackCount: data.summary.paystackCount || 0,
      paystackAmount: data.summary.paystackAmount || 0,
      totalCategories: data.contestAnalytics?.reduce((s, c) => s + (c.categoryCount || 0), 0) || 0,
      totalContestants: data.contestAnalytics?.reduce((s, c) => s + (c.contestantCount || 0), 0) || 0,
      pausedContests: data.contestAnalytics?.filter(c => c.contest?.status === 'PAUSED').length || 0,
      draftContests: data.contestAnalytics?.filter(c => c.contest?.status === 'DRAFT').length || 0,
      endedContests: data.contestAnalytics?.filter(c => c.contest?.status === 'ENDED').length || 0,
    });

    // setTopContests((data.contestAnalytics || []).map(c => {
    //   const vp   = c.contest?.votePrice || 1;
    //   const paid = Math.round((c.revenue || 0) / vp);
    //   return {
    //     id: c.contest.id, title: c.contest.title, status: c.contest.status, votePrice: vp,
    //     votes: c.voteCount || 0, paidVotes: paid, manualVotes: Math.max(0, (c.voteCount || 0) - paid),
    //     revenue: c.revenue || 0, paymentCount: c.paymentCount || 0, contestantCount: c.contestantCount || 0,
    //   };
    // }));

    setTopContests((data.contestAnalytics || []).map(c => {
      const vp = c.contest?.votePrice || 1;
      const paid = Math.round((c.revenue || 0) / vp);
      return {
        id: c.contest.id, title: c.contest.title, status: c.contest.status, votePrice: vp,
        votes: c.voteCount || 0, paidVotes: paid, manualVotes: Math.max(0, (c.voteCount || 0) - paid),
        revenue: c.revenue || 0, paymentCount: c.paymentCount || 0, contestantCount: c.contestantCount || 0,
        failedPayments: c.failedPayments || 0,
        failedAmount: c.failedAmount || 0,
        pendingPayments: c.pendingPayments || 0,
        pendingAmount: c.pendingAmount || 0,
        completedAmount: c.completedAmount || 0,
      };
    }));

    setRecentActivity({ payments: data.recentActivity?.payments || [], votes: data.recentActivity?.votes || [] });
  };

  // ── Company / Sub Admin ─────────────────────────────────────────────────────
  const fetchUserAnalytics = async () => {
    let hosts = [], allContests = [];

    // Step 1: hosts (for count only)
    try {
      const r = await hostService.getMyHosts();
      hosts = r?.data?.hosts || r?.data || r?.hosts || [];
      if (!Array.isArray(hosts) && hosts?.hosts) hosts = hosts.hosts;
    } catch { }

    // Step 2: contests (primary source)
    try {
      const r = await contestService.getUserContests({ page: 1, pageSize: 100 });
      if (Array.isArray(r?.data)) allContests = r.data;
      else if (Array.isArray(r?.data?.contests)) allContests = r.data.contests;
      else if (Array.isArray(r?.contests)) allContests = r.contests;
    } catch { }

    if (allContests.length === 0) {
      setStats({ totalHosts: hosts.length, totalContests: 0, activeContests: 0, totalCategories: 0, totalContestants: 0, totalVotes: 0, paidVotes: 0, manualVotes: 0, totalRevenue: 0, totalPayments: 0, pausedContests: 0, draftContests: 0, endedContests: 0 });
      return;
    }

    // Step 3: analytics per contest
    // const agg = { totalVotes: 0, paidVotes: 0, manualVotes: 0, totalRevenue: 0, totalPayments: 0, totalCategories: 0, totalContestants: 0, activeContests: 0, pausedContests: 0, draftContests: 0, endedContests: 0 };
    const agg = { totalVotes: 0, paidVotes: 0, manualVotes: 0, totalRevenue: 0, totalPayments: 0, failedPayments: 0, pendingPayments: 0, totalCategories: 0, totalContestants: 0, activeContests: 0, pausedContests: 0, draftContests: 0, endedContests: 0, ussdCount: 0, ussdAmount: 0, paystackCount: 0, paystackAmount: 0 };
    const details = [];

    for (let i = 0; i < allContests.length; i += 3) {
      const batch = allContests.slice(i, i + 3);
      const results = await Promise.allSettled(batch.map(c => analyticsService.getContestAnalytics(c.id)));
      results.forEach((r, idx) => {
        const c = batch[idx];
        if (r.status === 'fulfilled') {
          const a = r.value?.data || r.value;
          const votes = parseInt(a?.totalVotes) || 0;
          const rev = parseFloat(a?.totalRevenue) || 0;
          const vp = c.votePrice || 1;
          const paid = Math.round(rev / vp);
          const manual = Math.max(0, votes - paid);

          agg.totalVotes += votes;
          agg.paidVotes += paid;
          agg.manualVotes += manual;
          agg.totalRevenue += rev;
          // agg.totalPayments    += a?.totalPayments || 0;
          agg.totalPayments += a?.totalPayments || 0;
          agg.failedPayments += a?.failedPayments || 0;
          agg.pendingPayments += a?.pendingPayments || 0;
          agg.ussdCount += a?.ussdCount || 0;
          agg.ussdAmount += a?.ussdAmount || 0;
          agg.paystackCount += a?.paystackCount || 0;
          agg.paystackAmount += a?.paystackAmount || 0;
          agg.totalContestants += a?.results?.length || 0;
          // ✅ Only count categories belonging to this contest
          agg.totalCategories += c._count?.categories || a?.contest?.categories?.length || 0;

          if (c.status === 'ACTIVE' && !c.isPaused) agg.activeContests++;
          if (c.isPaused) agg.pausedContests++;
          if (c.status === 'DRAFT') agg.draftContests++;
          if (c.status === 'ENDED') agg.endedContests++;

          // details.push({
          //   id: c.id, title: c.title, status: c.status, votePrice: vp,
          //   votes, paidVotes: paid, manualVotes: manual,
          //   revenue: rev, paymentCount: a?.totalPayments || 0,
          // });
          details.push({
            id: c.id, title: c.title, status: c.status, votePrice: vp,
            votes, paidVotes: paid, manualVotes: manual,
            revenue: rev, paymentCount: a?.totalPayments || 0,
            failedPayments: a?.failedPayments || 0,
            failedAmount: a?.failedAmount || 0,
            pendingPayments: a?.pendingPayments || 0,
            pendingAmount: a?.pendingAmount || 0,
            completedAmount: a?.completedAmount || 0,
          });
        }
      });
    }

    const uniqueHostIds = new Set(allContests.map(c => c.hostId).filter(Boolean));
    setStats({ totalHosts: hosts.length > 0 ? hosts.length : uniqueHostIds.size, totalContests: allContests.length, ...agg });
    setTopContests(details.sort((a, b) => b.votes - a.votes));
  };

  // Open live results for a contest
  const openResults = (contest) => {
    setSelectedContest(contest);
    setModal(null);
    setShowResults(true);
  };

  const getPageTitle = () => isSuperAdmin ? 'System Statistics' : isCompanyAdmin ? 'Your Statistics' : 'Statistics Dashboard';
  const getPageSubtitle = () => isSuperAdmin ? 'System-wide analytics' : isCompanyAdmin ? 'Your contest performance and analytics' : 'Analytics for contests you manage';

  if (loading) {
    return (
      <DashboardLayout title={getPageTitle()}>
        <div className="flex flex-col items-center justify-center min-h-96">
          <Loader size="lg" text="Loading statistics..." />
          <p className="text-sm text-gray-600 mt-4">This may take a moment...</p>
        </div>
      </DashboardLayout>
    );
  }

  const byRevenue = [...topContests].sort((a, b) => b.revenue - a.revenue);
  const byVotes = [...topContests].sort((a, b) => b.votes - a.votes);
  const maxRev = byRevenue[0]?.revenue || 1;
  const maxVotes = byVotes[0]?.votes || 1;
  const medals = ['🥇', '🥈', '🥉'];

  // Shared contest row used in votes + revenue modals
  const ContestRow = ({ c, index, showRevenue }) => (
    <div onClick={() => openResults(c)}
      className="bg-gray-50 border border-gray-100 rounded-xl p-4 cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-colors group">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0 w-6 text-center">
            {index < 3 ? <span className="text-base">{medals[index]}</span> : <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>}
          </span>
          <span className="font-semibold text-gray-800 truncate">{c.title}</span>
          <StatusBadge status={c.status} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`font-bold ${showRevenue ? 'text-green-600' : 'text-blue-600'}`}>
            {showRevenue ? formatCurrency(c.revenue) : c.votes.toLocaleString()}
          </span>
          <span className="text-xs text-blue-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            View results →
          </span>
        </div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${showRevenue ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-blue-400 to-indigo-500'}`}
          style={{ width: `${showRevenue ? (maxRev > 0 ? c.revenue / maxRev * 100 : 0) : (maxVotes > 0 ? c.votes / maxVotes * 100 : 0)}%` }} />
      </div>
      <div className="flex justify-between mt-1.5 text-xs text-gray-400">
        <span className="text-green-600 font-medium">
          ✓ {c.paidVotes.toLocaleString()} paid{c.manualVotes > 0 ? ` · ✎ ${c.manualVotes} manual` : ''}
        </span>
        <span>{showRevenue ? `${c.paymentCount} payments` : formatCurrency(c.revenue)}</span>
      </div>
    </div>
  );

  return (
    <DashboardLayout title={getPageTitle()} subtitle={getPageSubtitle()}>

      {/* Live Results Modal — z-60 so it sits above the detail modals */}
      <LiveResultsModal
        open={showResults}
        onClose={() => { setShowResults(false); setSelectedContest(null); }}
        contest={selectedContest}
      />

      {/* ── Votes modal ─────────────────────────────────────────────── */}
      <DetailModal open={modal === 'votes'} onClose={() => setModal(null)} title="Vote Breakdown by Contest">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide mb-0.5">Total Votes</p>
              <p className="text-3xl font-bold text-blue-700">{stats.totalVotes.toLocaleString()}</p>
            </div>
            <div className="text-right text-xs text-gray-500 space-y-0.5">
              <p>{stats.totalContestants} contestants</p>
              <p>{stats.totalContests} contests</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
              <p className="text-xs text-gray-400 mb-0.5">Paid votes</p>
              <p className="text-lg font-bold text-green-600">{stats.paidVotes.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{formatCurrency(stats.totalRevenue)} revenue</p>
            </div>
            <div className="bg-white rounded-lg px-3 py-2 border border-purple-100">
              <p className="text-xs text-gray-400 mb-0.5">Manual votes</p>
              <p className="text-lg font-bold text-purple-600">{stats.manualVotes.toLocaleString()}</p>
              <p className="text-xs text-gray-400">No payment attached</p>
            </div>
          </div>
        </div>
        {stats.manualVotes > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2.5 mb-3 text-xs text-purple-700">
            <strong>{stats.manualVotes} manual vote{stats.manualVotes !== 1 ? 's' : ''}</strong> added without payment. Check <strong>Votes → Manual Votes</strong>.
          </div>
        )}
        <p className="text-xs text-blue-500 font-medium mb-3">👆 Click any contest to view full leaderboard &amp; export</p>
        <div className="space-y-3">
          {byVotes.map((c, i) => <ContestRow key={c.id} c={c} index={i} showRevenue={false} />)}
        </div>
      </DetailModal>

      {/* ── Revenue modal ────────────────────────────────────────────── */}
      <DetailModal open={modal === 'revenue'} onClose={() => setModal(null)} title="Revenue Breakdown by Contest">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-green-700 font-semibold uppercase tracking-wide mb-0.5">Total Revenue</p>
            <p className="text-3xl font-bold text-green-700">{formatCurrency(stats.totalRevenue)}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.paidVotes.toLocaleString()} paid votes</p>
          </div>
          <div className="text-right text-xs text-gray-500 space-y-0.5">
            <p>{stats.totalPayments} payments</p>
            <p>{stats.totalContests} contests</p>
          </div>
        </div>
        {/* Payment method breakdown */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
            <p className="text-xs text-gray-400 mb-0.5">📱 USSD (MoMo)</p>
            <p className="text-lg font-bold text-blue-600">{(stats.ussdCount || 0).toLocaleString()}</p>
            <p className="text-xs text-green-600 font-semibold">{formatCurrency(stats.ussdAmount || 0)}</p>
          </div>
          <div className="bg-white rounded-lg px-3 py-2 border border-orange-100">
            <p className="text-xs text-gray-400 mb-0.5">💳 Paystack (Card)</p>
            <p className="text-lg font-bold text-orange-600">{(stats.paystackCount || 0).toLocaleString()}</p>
            <p className="text-xs text-green-600 font-semibold">{formatCurrency(stats.paystackAmount || 0)}</p>
          </div>
        </div>

        <p className="text-xs text-blue-500 font-medium mb-3">👆 Click any contest to view full leaderboard &amp; export</p>
        <div className="space-y-3">
          {byRevenue.map((c, i) => <ContestRow key={c.id} c={c} index={i} showRevenue={true} />)}
        </div>




        {/* Failed Transactions Modal */}
        {/* <DetailModal open={modal === 'failed'} onClose={() => setModal(null)} title="Failed Transactions by Contest">
  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-red-700 font-semibold uppercase tracking-wide mb-0.5">Total Failed</p>
        <p className="text-3xl font-bold text-red-600">{stats.failedPayments.toLocaleString()}</p>
      </div>
      <div className="text-right text-xs text-gray-500 space-y-0.5">
        <p className="text-yellow-600 font-semibold">{stats.pendingPayments} pending</p>
        <p className="text-green-600 font-semibold">{stats.totalPayments} successful</p>
      </div>
    </div>
  </div>
  <div className="space-y-3">
    {topContests.map((c, i) => (
      <div key={c.id} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-gray-400 font-mono flex-shrink-0">#{i + 1}</span>
            <span className="font-semibold text-gray-800 truncate">{c.title}</span>
            <StatusBadge status={c.status} />
          </div>
          <span className="font-bold text-red-600 flex-shrink-0">{(c.failedPayments || 0).toLocaleString()} failed</span>
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span className="text-green-600">✓ {c.paymentCount} successful</span>
          <span className="text-yellow-600">{(c.pendingPayments || 0)} pending</span>
        </div>
      </div>
    ))}
  </div>
        </DetailModal> */}
      </DetailModal>

      <DetailModal open={modal === 'failed'} onClose={() => setModal(null)} title="Failed Transactions by Contest">
        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
            <p className="text-xs text-red-600 font-semibold uppercase tracking-wide mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-600">{stats.failedPayments.toLocaleString()}</p>
            <p className="text-xs text-red-500 font-semibold mt-0.5">{formatCurrency(stats.failedAmount || 0)}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
            <p className="text-xs text-yellow-600 font-semibold uppercase tracking-wide mb-1">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pendingPayments.toLocaleString()}</p>
            <p className="text-xs text-yellow-500 font-semibold mt-0.5">{formatCurrency(stats.pendingAmount || 0)}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <p className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-1">Successful</p>
            <p className="text-2xl font-bold text-green-600">{stats.totalPayments.toLocaleString()}</p>
            <p className="text-xs text-green-500 font-semibold mt-0.5">{formatCurrency(stats.completedAmount || stats.totalRevenue || 0)}</p>
          </div>
        </div>

        {/* Per-contest breakdown */}
        <div className="space-y-3">
          {topContests.map((c, i) => (
            <div key={c.id} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-gray-400 font-mono flex-shrink-0">#{i + 1}</span>
                  <span className="font-semibold text-gray-800 truncate">{c.title}</span>
                  <StatusBadge status={c.status} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-red-500 font-semibold mb-0.5">Failed</p>
                  <p className="text-sm font-bold text-red-600">{(c.failedPayments || 0).toLocaleString()}</p>
                  <p className="text-xs text-red-400">{formatCurrency(c.failedAmount || 0)}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-yellow-500 font-semibold mb-0.5">Pending</p>
                  <p className="text-sm font-bold text-yellow-600">{(c.pendingPayments || 0).toLocaleString()}</p>
                  <p className="text-xs text-yellow-400">{formatCurrency(c.pendingAmount || 0)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-green-500 font-semibold mb-0.5">Successful</p>
                  <p className="text-sm font-bold text-green-600">{(c.paymentCount || 0).toLocaleString()}</p>
                  <p className="text-xs text-green-400">{formatCurrency(c.completedAmount || c.revenue || 0)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DetailModal>

      {/* ── Contests modal ───────────────────────────────────────────── */}
      <DetailModal open={modal === 'contests'} onClose={() => setModal(null)} title="All Contests">
        <p className="text-xs text-blue-500 font-medium mb-3">👆 Click any row to view full leaderboard &amp; export</p>
        {topContests.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No contest data available.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {['#', 'Contest', 'Status', 'Votes', 'Paid', 'Manual', 'Revenue', 'Pmts'].map(h => (
                  <th key={h} className="text-left py-2 pr-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {topContests.map((c, i) => (
                <tr key={c.id} onClick={() => openResults(c)} className="hover:bg-blue-50 transition-colors cursor-pointer group">
                  <td className="py-3 pr-3 text-gray-400 text-xs font-mono">{i + 1}</td>
                  <td className="py-3 pr-3 font-medium text-gray-900 max-w-[140px] truncate group-hover:text-blue-600">{c.title}</td>
                  <td className="py-3 pr-3"><StatusBadge status={c.status} /></td>
                  <td className="py-3 pr-3 font-bold text-gray-900">{c.votes.toLocaleString()}</td>
                  <td className="py-3 pr-3 text-green-600 font-semibold">{c.paidVotes.toLocaleString()}</td>
                  <td className="py-3 pr-3">{c.manualVotes > 0 ? <span className="text-purple-600 font-semibold">{c.manualVotes}</span> : <span className="text-gray-300">—</span>}</td>
                  <td className="py-3 pr-3 font-semibold text-green-600">{formatCurrency(c.revenue)}</td>
                  <td className="py-3 text-gray-500">{c.paymentCount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50">
                <td colSpan="3" className="pt-3 pb-2 font-bold text-gray-700">Total</td>
                <td className="pt-3 pb-2 font-bold text-gray-900">{stats.totalVotes.toLocaleString()}</td>
                <td className="pt-3 pb-2 font-bold text-green-700">{stats.paidVotes.toLocaleString()}</td>
                <td className="pt-3 pb-2 font-bold text-purple-600">{stats.manualVotes > 0 ? stats.manualVotes.toLocaleString() : '—'}</td>
                <td className="pt-3 pb-2 font-bold text-green-700">{formatCurrency(stats.totalRevenue)}</td>
                <td className="pt-3 pb-2 font-bold text-gray-700">{stats.totalPayments}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </DetailModal>

      {/* ════════════ PAGE BODY ════════════ */}

      {/* Role badge */}
      <div className="mb-4">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-red-100 text-red-800' : isCompanyAdmin ? 'bg-blue-100 text-blue-800' : isSubAdmin ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {isSuperAdmin && '🔴 Super Admin View'}{isCompanyAdmin && '🟡 Company Admin View'}{isSubAdmin && '🟢 Sub Admin View'}
        </span>
      </div>

      {/* Key metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <ClickableCard colorClass="border-blue-500" onClick={() => setModal('contests')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{isSuperAdmin ? 'Total Contests' : 'Your Contests'}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalContests}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.activeContests} active · {stats.pausedContests} paused</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
          </div>
        </ClickableCard>

        <ClickableCard colorClass="border-green-500" onClick={() => setModal('votes')}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">Total Votes</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalVotes.toLocaleString()}</p>
              <BreakdownRow label="Paid votes" value={stats.paidVotes.toLocaleString()} color="text-green-600" dotColor="#10b981" />
              <BreakdownRow label="Manual votes" value={stats.manualVotes.toLocaleString()} color="text-purple-600" dotColor="#9333ea" />
            </div>
            <div className="bg-green-100 rounded-full p-3 flex-shrink-0 ml-2">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            </div>
          </div>
        </ClickableCard>

        <ClickableCard colorClass="border-orange-500" onClick={() => setModal('revenue')}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900">
                {new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS', minimumFractionDigits: 2 }).format(stats.totalRevenue)}
              </p>
              <BreakdownRow label="Paid votes" value={stats.paidVotes.toLocaleString()} color="text-green-600" dotColor="#10b981" />
              <BreakdownRow label="Manual votes" value={stats.manualVotes.toLocaleString()} color="text-purple-600" dotColor="#9333ea" />
            </div>
            <div className="bg-orange-100 rounded-full p-3 flex-shrink-0 ml-2">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
          </div>
        </ClickableCard>

        {/* Failed Transactions Card */}
        <ClickableCard onClick={() => setModal('failed')} colorClass="border-red-400">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Failed Transactions</p>
            <span className="text-2xl">❌</span>
          </div>
          <p className="text-3xl font-bold text-red-600">{stats.failedPayments.toLocaleString()}</p>
          <BreakdownRow label="Pending" value={stats.pendingPayments} color="text-yellow-600" dotColor="#eab308" />
          <BreakdownRow label="Successful" value={stats.totalPayments} color="text-green-600" dotColor="#22c55e" />
        </ClickableCard>
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active', value: stats.activeContests, icon: '▶', bg: 'from-green-50 to-green-100', text: 'text-green-700', dot: 'bg-green-500' },
          { label: 'Paused', value: stats.pausedContests, icon: '⏸', bg: 'from-yellow-50 to-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
          { label: 'Draft', value: stats.draftContests, icon: '📝', bg: 'from-gray-50 to-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' },
          { label: 'Ended', value: stats.endedContests, icon: '✓', bg: 'from-red-50 to-red-100', text: 'text-red-700', dot: 'bg-red-500' },
        ].map(({ label, value, icon, bg, text, dot }) => (
          <Card key={label} className={`bg-gradient-to-br ${bg}`}>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">{label}</p><p className={`text-2xl font-bold ${text}`}>{value}</p></div>
              <div className={`w-10 h-10 ${dot} rounded-full flex items-center justify-center`}><span className="text-white">{icon}</span></div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <h3 className="text-sm font-semibold text-gray-600 mb-2">Categories</h3>
          <p className="text-4xl font-bold text-blue-600">{stats.totalCategories}</p>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-gray-600 mb-2">Avg Revenue / Contest</h3>
          <p className="text-4xl font-bold text-green-600">{formatCurrency(stats.totalContests > 0 ? stats.totalRevenue / stats.totalContests : 0)}</p>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-gray-600 mb-2">Avg Votes / Contest</h3>
          <p className="text-4xl font-bold text-purple-600">{stats.totalContests > 0 ? Math.round(stats.totalVotes / stats.totalContests) : 0}</p>
        </Card>
      </div>

      {/* Recent Activity — Super Admin only */}
      {isSuperAdmin && recentActivity.payments.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
            <div className="space-y-3">
              {recentActivity.payments.slice(0, 5).map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div><p className="font-medium text-gray-900">{p.contest?.title || 'Unknown'}</p><p className="text-sm text-gray-600">{p.voterEmail || p.voterPhone}</p></div>
                  <div className="text-right"><p className="font-semibold text-green-600">{formatCurrency(p.amount)}</p><p className="text-xs text-gray-500">{p.voteCount} votes</p></div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Votes</h3>
            <div className="space-y-3">
              {recentActivity.votes.slice(0, 5).map(v => (
                <div key={v.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div><p className="font-medium text-gray-900">{v.contest?.title || 'Unknown'}</p><p className="text-sm text-gray-600">{v.contestant?.name || 'Unknown'}</p></div>
                  <div className="text-right"><p className="font-semibold text-blue-600">{v.voteCount} votes</p><p className="text-xs text-gray-500">{v.voterEmail || v.voterPhone}</p></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {stats.totalContests === 0 && (
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No statistics available</h3>
            <p className="mt-1 text-gray-500">{isSubAdmin ? "You don't have access to any contests yet." : 'Create your first contest to start seeing statistics here.'}</p>
          </div>
        </Card>
      )}

    </DashboardLayout>
  );
};

export default StatisticsPage;